import type { CharacterRuntimeState } from '../character/runtime-state';
import type { CharacterState } from '../character/character-state';
import type { CharacterPresentationSnapshot, Facing } from '../contracts/presentation-snapshot';
import type { TransitionReason } from '../character/transition';
import type { BattleRuntimeState } from './battle-runtime-state';
import type { BattlePhase } from './battle-phase';
import type { RoundResult } from '../combat/systems';

export interface CharacterBattleSnapshot extends CharacterPresentationSnapshot {
  readonly characterDefinitionId: string;
  readonly facing: Facing;
  readonly state: CharacterState;
  readonly lastTransitionReason: TransitionReason;
  readonly velocity: Readonly<{ x: number; y: number }>;
  readonly isGrounded: boolean;
  readonly hp: number;
  readonly maxHp: number;
  readonly gauge: number;
  readonly postWakeInvincibilityFrames: number;
}

export interface BattleSnapshot {
  readonly logicalFrame: number;
  readonly battlePhase: BattlePhase;
  readonly timerFrames: number;
  readonly hitstopFrames: number;
  readonly characters: readonly [CharacterBattleSnapshot, CharacterBattleSnapshot];
  readonly roundResult: RoundResult | null;
  readonly activeCinematicId: string | null;
}

const projectCharacter = (
  character: Readonly<CharacterRuntimeState>,
  hitstopFrames: number,
): CharacterBattleSnapshot => Object.freeze({
  id: character.id,
  characterDefinitionId: character.characterDefinitionId,
  position: Object.freeze({ ...character.position }),
  velocity: Object.freeze({ ...character.velocity }),
  isGrounded: character.isGrounded,
  hp: character.hp,
  maxHp: character.maxHp,
  gauge: character.gauge,
  postWakeInvincibilityFrames: character.postWakeInvincibilityFrames,
  facing: character.facing,
  state: character.state,
  stateFrame: character.stateFrame,
  lastTransitionReason: character.lastTransitionReason,
  currentAttackId: character.currentAttack?.id ?? null,
  attackElapsedFrames: character.currentAttack?.elapsedFrames ?? null,
  hitstopFrames,
  isAirborneInvincible: character.isAirborneInvincible,
});

export const projectBattleSnapshot = (runtime: Readonly<BattleRuntimeState>): BattleSnapshot => {
  const characters = Object.freeze([
    projectCharacter(runtime.characters[0], runtime.hitstopFrames),
    projectCharacter(runtime.characters[1], runtime.hitstopFrames),
  ] as const);
  return Object.freeze({
    logicalFrame: runtime.logicalFrame,
    battlePhase: runtime.battlePhase,
    timerFrames: runtime.timerFrames,
    hitstopFrames: runtime.hitstopFrames,
    characters,
    roundResult: runtime.roundResult === null ? null : Object.freeze({ ...runtime.roundResult }),
    activeCinematicId: runtime.activeCinematicId,
  });
};
